import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Heart, MessageCircle, Share2, Sparkles, ChevronUp, ChevronDown,
  MoreHorizontal, Flag, Ban, Pencil, Trash2, Pin, PinOff
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import ReportDialog from '../ReportDialog';
import BlockUserDialog from '../BlockUserDialog';
import ShareButtons from '../ShareButtons';
import LinkPreviewCard from '../LinkPreviewCard';
import { TagList } from '../TagBadge';
import BetaBadge from '../BetaBadge';
import { toast } from 'sonner';
import { EditPostDialog, DeletePostDialog, PinPostDialog } from './PostCardDialogs';
import TranslatePopover from './TranslatePopover';
import PostImageGrid from './PostImageGrid';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getCategoryColor = (category) => {
  const colors = {
    'general': 'bg-muted text-muted-foreground',
    'care-tips': 'bg-primary/10 text-primary',
    'identification': 'bg-info/10 text-info',
    'showcase': 'bg-secondary/10 text-secondary',
    'question': 'bg-warning/10 text-warning',
    'news': 'bg-accent text-accent-foreground'
  };
  return colors[category] || colors.general;
};

export function PostCard({
  post, user, userVote, isLiked, onVote, onLike, onBlock, onRefresh
}) {
  const navigate = useNavigate();
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPinDialog, setShowPinDialog] = useState(false);
  const [pinDuration, setPinDuration] = useState('7d');
  const [editForm, setEditForm] = useState({ title: post.title, content: post.content });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [pinning, setPinning] = useState(false);
  // Translation overlay — when non-null, render the translated title/body
  // in place of the original; user can flip back with "Show original".
  const [translated, setTranslated] = useState(null); // { lang, title, body } | null

  const isOwner = user && user.id === post.author_id;
  const isAdmin = user && (user.is_admin || user.is_sysadmin || user.is_operator_admin);

  const handlePinPost = async (duration = '7d') => {
    setPinning(true);
    try {
      if (post.is_pinned) {
        const response = await fetch(`${API}/posts/${post.id}/pin`, { method: 'DELETE', credentials: 'include' });
        if (response.ok) { toast.success('Post unpinned'); onRefresh?.(); }
        else toast.error('Failed to unpin');
      } else {
        const response = await fetch(`${API}/posts/${post.id}/pin?duration=${duration}`, { method: 'POST', credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          let message = 'Post pinned to top';
          if (data.unpinned_post) message += ` (auto-unpinned: "${data.unpinned_post}")`;
          toast.success(message);
          onRefresh?.();
        } else toast.error('Failed to pin');
      }
    } catch { toast.error('Failed to pin/unpin'); }
    finally { setPinning(false); setShowPinDialog(false); }
  };

  const handleEditPost = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/posts/${post.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(editForm),
      });
      if (response.ok) { toast.success('Post updated'); setShowEditDialog(false); onRefresh?.(); }
      else toast.error('Failed to update');
    } catch { toast.error('Failed to update'); }
    finally { setSaving(false); }
  };

  const handleDeletePost = async () => {
    setDeleting(true);
    try {
      const response = await fetch(`${API}/posts/${post.id}`, { method: 'DELETE', credentials: 'include' });
      if (response.ok) { toast.success('Post deleted'); setShowDeleteDialog(false); onRefresh?.(); }
      else toast.error('Failed to delete');
    } catch { toast.error('Failed to delete'); }
    finally { setDeleting(false); }
  };

  return (
    <Card className={`card-interactive ${post.is_pinned ? 'ring-2 ring-blue-400/40' : ''}`} data-testid={`post-card-${post.id}`} data-test-class="feed-post-card">
      <CardHeader className="pb-2 cursor-pointer" onClick={() => navigate(`/post/${post.id}`)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Avatar className="w-10 h-10 cursor-pointer hover:ring-2 hover:ring-primary transition-all" onClick={(e) => { e.stopPropagation(); navigate(`/profile/${post.author_username}`); }}>
              <AvatarImage src={post.pet_avatar_url || post.author_avatar} />
              <AvatarFallback>{post.author_username?.charAt(0)}</AvatarFallback>
            </Avatar>
            <div>
              <p className="font-medium hover:underline cursor-pointer flex items-center gap-1" onClick={(e) => { e.stopPropagation(); navigate(`/profile/${post.author_username}`); }}>
                @{post.author_username}
                {post.author_is_beta_user && <BetaBadge size="md" />}
              </p>
              <p className="text-xs text-muted-foreground">
                {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {post.is_pinned && (
              <Badge className="bg-gradient-to-r from-blue-500 to-purple-500 text-white border-0" data-testid={`pinned-badge-${post.id}`}>
                <Pin className="w-3 h-3 mr-1" /> Pinned
              </Badge>
            )}
            <Badge className={getCategoryColor(post.category)}>{post.category}</Badge>
            {post.is_trending && (
              <Badge className="bg-gradient-to-r from-orange-500 to-red-500 text-white border-0" data-testid={`trending-badge-${post.id}`}>
                <Sparkles className="w-3 h-3 mr-1" /> Trending
              </Badge>
            )}
            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`post-menu-${post.id}`}>
                    <MoreHorizontal className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                  {isAdmin && (
                    <>
                      <DropdownMenuItem onClick={() => post.is_pinned ? handlePinPost() : setShowPinDialog(true)} disabled={pinning} data-testid={`pin-post-${post.id}`}>
                        {post.is_pinned ? <><PinOff className="w-4 h-4 mr-2" />Unpin Post</> : <><Pin className="w-4 h-4 mr-2" />Pin to Top...</>}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  {isOwner && (
                    <>
                      <DropdownMenuItem onClick={() => { setEditForm({ title: post.title, content: post.content }); setShowEditDialog(true); }} data-testid={`edit-post-${post.id}`}>
                        <Pencil className="w-4 h-4 mr-2" /> Edit Post
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setShowDeleteDialog(true)} className="text-destructive focus:text-destructive" data-testid={`delete-post-${post.id}`}>
                        <Trash2 className="w-4 h-4 mr-2" /> Delete Post
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  {!isOwner && (
                    <>
                      <ReportDialog contentType="post" contentId={post.id} trigger={
                        <DropdownMenuItem onSelect={(e) => e.preventDefault()} data-testid={`report-post-${post.id}`}>
                          <Flag className="w-4 h-4 mr-2" /> Report Post
                        </DropdownMenuItem>
                      } />
                      <BlockUserDialog userId={post.author_id} username={post.author_username} onBlock={onBlock} trigger={
                        <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                          <Ban className="w-4 h-4 mr-2" /> Block @{post.author_username}
                        </DropdownMenuItem>
                      } />
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="cursor-pointer" onClick={() => navigate(`/post/${post.id}`)}>
        <h3 className="text-lg font-semibold mb-2" data-testid={`post-title-${post.id}`}>
          {translated?.title || post.title}
        </h3>
        <p className="text-muted-foreground leading-relaxed line-clamp-4" data-testid={`post-content-${post.id}`}>
          {translated?.body || post.content}
        </p>
        {translated && (
          <div
            className="mt-1 text-xs text-muted-foreground italic"
            data-testid={`translated-badge-${post.id}`}
          >
            Translated to {translated.lang}
          </div>
        )}
        {post.link_preview && <div className="mt-3" onClick={(e) => e.stopPropagation()}><LinkPreviewCard preview={post.link_preview} /></div>}
        {(post.images?.length > 0 || post.image_url) && (
          <div className="mt-3" onClick={(e) => e.stopPropagation()}>
            <PostImageGrid images={post.images} imageUrl={post.image_url} alt={post.title} />
          </div>
        )}
        {post.video_url && (
          <div className="mt-3" onClick={(e) => e.stopPropagation()} data-testid={`post-video-${post.id}`}>
            <video
              src={post.video_url}
              controls
              preload="metadata"
              playsInline
              className="w-full max-h-[480px] rounded-xl bg-black"
            />
          </div>
        )}
        {post.tags && post.tags.length > 0 && <div className="mt-3"><TagList tags={post.tags} /></div>}

        {/* Actions Bar */}
        <div className="flex items-center gap-4 mt-4 pt-3 border-t" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-1">
            <button className={`p-1 rounded transition-colors ${userVote === 'up' ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-primary hover:bg-primary/5'}`} onClick={(e) => onVote(post.id, 'up', e)} data-testid={`upvote-btn-${post.id}`} data-test-class="feed-upvote-btn">
              <ChevronUp className="w-5 h-5" />
            </button>
            <span className={`text-sm font-medium min-w-[24px] text-center ${(post.score || 0) > 0 ? 'text-primary' : (post.score || 0) < 0 ? 'text-destructive' : 'text-muted-foreground'}`} data-testid={`vote-count-${post.id}`}>{post.score || 0}</span>
            <button className={`p-1 rounded transition-colors ${userVote === 'down' ? 'text-destructive bg-destructive/10' : 'text-muted-foreground hover:text-destructive hover:bg-destructive/5'}`} onClick={(e) => onVote(post.id, 'down', e)} data-testid={`downvote-btn-${post.id}`}>
              <ChevronDown className="w-5 h-5" />
            </button>
          </div>
          <button className={`flex items-center gap-2 text-sm transition-colors ${isLiked ? 'text-secondary' : 'text-muted-foreground hover:text-secondary'}`} onClick={(e) => { e.stopPropagation(); onLike(post.id); }} data-testid={`like-btn-${post.id}`}>
            <Heart className={`w-4 h-4 ${isLiked ? 'fill-current' : ''}`} /> {post.likes_count}
          </button>
          <button className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors">
            <MessageCircle className="w-4 h-4" /> {post.comments_count}
          </button>
          {(post.share_count || 0) > 0 && (
            <span className="flex items-center gap-1 text-sm text-muted-foreground" data-testid={`share-count-${post.id}`}>
              <Share2 className="w-4 h-4" /> {post.share_count}
            </span>
          )}
          <TranslatePopover
            title={post.title}
            body={post.content}
            onTranslated={setTranslated}
            testId={`translate-${post.id}`}
          />
          <div className="ml-auto" onClick={(e) => e.stopPropagation()}>
            <ShareButtons url={`/post/${post.id}`} title={post.title} description={post.content?.substring(0, 100)} source="feed" compact showRepost={!!user} onRepost={async (comment) => {
              const response = await fetch(`${API}/shares/repost`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ original_post_id: post.id, comment }) });
              if (!response.ok) throw new Error('Failed to repost');
              onRefresh?.();
            }} />
          </div>
        </div>
      </CardContent>

      <EditPostDialog open={showEditDialog} onOpenChange={setShowEditDialog} editForm={editForm} setEditForm={setEditForm} onSave={handleEditPost} saving={saving} />
      <DeletePostDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog} onDelete={handleDeletePost} deleting={deleting} />
      <PinPostDialog open={showPinDialog} onOpenChange={setShowPinDialog} pinDuration={pinDuration} setPinDuration={setPinDuration} onPin={handlePinPost} pinning={pinning} />
    </Card>
  );
}

export default PostCard;
